import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { ReactReduxContext } from 'react-redux';

import AnalyticsManager from 'ia-react-core/~/services/analytics/AnalyticsManager';
import Router from 'ia-react-core/~/services/router/Router';
import get from 'ia-react-core/~/utils/accessors/get';
import REDUX from 'ia-react-core/~/constants/REDUX';
import { Action } from '@remix-run/router';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import AnalyticsContext from '../../../components/analytics/_AnalyticsContext';

const TRACKED_ELEMENTS = ['BUTTON', 'A'];
const TRACKED_ROLES = ['button', 'listbox', 'option', 'tab'];
const ANALYTICS_ID_ATTRIBUTE = 'data-analytics-id';
const ANALYTICS_SCOPE_ATTRIBUTE = 'data-analytics-scope';
const ANALYTICS_VALUES_ATTRIBUTE = 'data-analytics-values';

export interface ConfigAnalyticsOptions {
  managers: AnalyticsManager[];
  ignoreInitialPageView?: boolean;
  delayPageViewUntil?: () => Promise<void>;
  pageViewDelay?: number;
}

export interface ConfigAnalyticsProps { }

const configAnalytics = ({ managers, ignoreInitialPageView, delayPageViewUntil, pageViewDelay = 100 }: ConfigAnalyticsOptions) => (WrappedComponent: FC<ConfigAnalyticsProps>) => {
  const ConfigAnalytics = (props: ConfigAnalyticsProps) => {
    const history = Router.useHistory();

    const { store } = useContext(ReactReduxContext);

    const { pageValues, values } = useContext(AnalyticsContext);

    const getFlattenedPageValues = useCallback(() => (
      Array.from(pageValues.values()).reduce((result, currentPageValues) => {
        Object.assign(result, currentPageValues);
        return result;
      }, {})
    ), [pageValues]);

    const track = useCallback((data: object) => {
      const flattenedPageValues = getFlattenedPageValues();
      managers.forEach((manager) => manager.track({ ...data, pageValues: flattenedPageValues }));
    }, [getFlattenedPageValues]);

    const pageView = useCallback((data: object) => {
      const flattenedPageValues = getFlattenedPageValues();
      managers.forEach((manager) => manager.pageView({ ...data, pageValues: flattenedPageValues }));
    }, [getFlattenedPageValues]);

    const analytics = useMemo(() => ({
      track,
      pageView,
    }), [pageView, track]);

    const newContext = useMemo(() => ({
      scope: [],
      analytics,
      pageValues,
      values,
    }), [analytics, pageValues, values]);

    const handleClick = useCallback((event: MouseEvent) => {
      const { target } = event as unknown as { target: HTMLInputElement };
      const trackedElement = (
        (
          (
            (
              TRACKED_ELEMENTS.includes(target.tagName) ||
              TRACKED_ROLES.includes(target.getAttribute('role')) ||
              (target.tagName === 'INPUT' && target.getAttribute('type') === 'submit')
            )
            && target
          ) ||
          target.closest(TRACKED_ELEMENTS.join(',')) ||
          target.closest(TRACKED_ROLES.map((role) => `[role="${role}"]`).join(','))
        )
      );

      const isTrackingDisabled = (
        target.getAttribute('data-analytics-disabled') === '' ||
        target.getAttribute('data-analytics-disabled') === 'true' ||
        target.closest('[data-analytics-disabled=""],[data-analytics-disabled="true"]')
      );

      if (trackedElement && !isTrackingDisabled) {
        const id = trackedElement.getAttribute(ANALYTICS_ID_ATTRIBUTE) || trackedElement.getAttribute('data-testid') || trackedElement.id || trackedElement.name;
        const closestScopedElement = target.closest(`[${ANALYTICS_SCOPE_ATTRIBUTE}]`);
        const scope = (closestScopedElement?.getAttribute(ANALYTICS_SCOPE_ATTRIBUTE) || '');
        const currentValue = JSON.parse((closestScopedElement?.getAttribute(ANALYTICS_VALUES_ATTRIBUTE) || '{}'));
        track({ event, id, scope, values: currentValue, pageValues: getFlattenedPageValues() });
      }
    }, [getFlattenedPageValues, track]);

    const checkIsPageLoading = useCallback(() => {
      const state = store.getState();
      const isPageLoading = Boolean(get(state, `${REDUX.ROUTE_REDUCER_KEY}.loadingPage`));
      return isPageLoading;
    }, [store]);

    const delayedPageView = useCallback(async () => {
      await Promise.resolve(delayPageViewUntil?.());

      // Give some time for the router to react to the route change
      await new Promise((resolve) => { setTimeout(resolve, pageViewDelay); });

      // If a page is loading, we want to wait for it to be complete so we can read
      while (checkIsPageLoading()) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          const unbsubscribe = store.subscribe(() => {
            resolve(null);
            unbsubscribe();
          });
        });
      }

      // Give a bit of time to render the page newly loaded
      await new Promise((resolve) => { setTimeout(resolve, pageViewDelay); });

      pageView({ location: history.location });
    }, [checkIsPageLoading, history, pageView, store]);

    useEffect(() => {
      if (!ignoreInitialPageView) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        delayedPageView();
      }
    // Only first render, do not want to call again if pageValues change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const unlisten = history.listen((location, action) => {
        if (action !== Action.Replace) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          delayedPageView();
        }
      });

      return () => {
        unlisten();
      };
    }, [delayedPageView, history]);

    useEffect(() => {
      const useCapture = true;
      document.addEventListener('click', handleClick, useCapture);

      return () => {
        document.removeEventListener('click', handleClick, useCapture);
      };
    }, [handleClick]);

    return (
      <AnalyticsContext.Provider value={newContext}>
        <WrappedComponent {...props} />
      </AnalyticsContext.Provider>
    );
  };

  ConfigAnalytics.displayName = getWrappedDisplayName('ConfigAnalytics', WrappedComponent);

  return hoistNonReactStatics(ConfigAnalytics, WrappedComponent);
};

/**
 * @component
 * @visibleName configAnalytics
 */
export default configAnalytics;
